import { enumerateDaysInPeriod, getPageId, Period, PeriodType } from '../Models';
import {
  AnalysisStatus,
  Emotion,
  EmotionAnalysis,
  EmotionAnalysisFeature,
  isAnalysisFailed,
  KeywordAnalysis,
  PageAnalysis,
  PagesMap,
} from '../../../Models';
import moment from 'moment';

export interface EmotionsAndTopicsReport {
  readonly emotions: ReadonlyArray<EmotionAnalysis>;
  readonly topics: ReadonlyArray<KeywordAnalysis>;
  readonly failedStatuses: ReadonlyArray<AnalysisStatus>;
  readonly className?: string;
}

const groupEmotions = (stats: PageAnalysis[], emotion: Emotion): EmotionAnalysis => {
  const groupedEmotions: EmotionAnalysis[] = [];

  const emotions: EmotionAnalysisFeature[] = stats.map(stat => stat.emotions);

  for (const emotionValues of emotions) {
    const analysis = emotionValues.analysis;
    if (analysis && analysis.length > 0) {
      groupedEmotions.push(...analysis);
    }
  }

  const filtered = groupedEmotions
    .filter(emotionAnalysis => emotionAnalysis.category === emotion)
    .map(em => em.relevance);

  const sumRelevance = filtered.reduce((acc, value) => acc + value, 0);

  return {
    relevance: sumRelevance / filtered.length || 0,
    category: emotion,
    subcategory: null,
    context: null,
    mixedEmotion: null,
  };
};

const groupConcepts = (stats: PageAnalysis[]): KeywordAnalysis[] => {
  const conceptsLoaded = stats
    .filter(value => {
      return value && value.concepts && value.concepts.status === AnalysisStatus.analyzed;
    })
    .map(analysis => analysis.concepts)
    .reduce((acc: KeywordAnalysis[], value) => {
      acc.push(...value.analysis);
      return acc;
    }, [])
    .sort((a, b) => b.relevance - a.relevance);

  const statsMap = conceptsLoaded.reduce((acc: { [key: string]: any }, value) => {
    const relevanceArray = acc[value.name] || Array<number>();
    relevanceArray.push(value.relevance);
    acc[value.name] = relevanceArray;
    return acc;
  }, {});

  const result: KeywordAnalysis[] = [];

  for (const key in statsMap) {
    if (statsMap.hasOwnProperty(key)) {
      const conceptArray: number[] = statsMap[key];
      result.push({
        relevance: conceptArray.reduce((acc, value) => acc + value, 0) / conceptArray.length,
        name: key,
      });
    }
  }

  return result;
};

export const getEmotionsAndTopicsReport = (period: Period, pages: PagesMap): EmotionsAndTopicsReport => {
  const analysis: PageAnalysis[] = [];

  enumerateDaysInPeriod(period, (date: Date) => {
    const pageId = getPageId(moment(date));
    const existingPage = pages[pageId];

    if (existingPage && existingPage.analysis) {
      analysis.push(existingPage.analysis);
    }
  });

  const statsWithEmotionsLoaded = analysis.filter(value => {
    return value && value.emotions && value.emotions.status === AnalysisStatus.analyzed;
  });

  const emotionsFailedStatuses = analysis
    .filter(value => {
      return value && value.emotions && value.emotions.status && isAnalysisFailed(value.emotions.status);
    })
    .map(value => {
      return value.emotions.status;
    });

  const conceptFailedStatuses = analysis
    .filter(value => {
      return value && value.concepts && value.concepts.status && isAnalysisFailed(value.concepts.status);
    })
    .map(value => {
      return value.concepts.status;
    });

  const failedStatuses = emotionsFailedStatuses.concat(conceptFailedStatuses);

  let emotion: EmotionAnalysis | null;
  if (period.type === PeriodType.DAY) {
    // @ts-ignore
    const emotions: EmotionAnalysis[] = statsWithEmotionsLoaded[0]
      ? statsWithEmotionsLoaded[0].emotions.analysis
      : null;
    if (emotions) {
      const sorted = emotions.sort((a, b) => b.relevance - a.relevance);
      emotion = sorted[0];
    } else {
      emotion = null;
    }
  } else {
    emotion = mostRelevantEmotion(statsWithEmotionsLoaded);
  }
  return {
    emotions: emotion ? [emotion] : [],
    topics: groupConcepts(analysis),
    failedStatuses,
  };
};

const mostRelevantEmotion = (stats: PageAnalysis[]): EmotionAnalysis | null => {
  const emotions: EmotionAnalysis[] = [
    groupEmotions(stats, Emotion.anger),
    groupEmotions(stats, Emotion.fear),
    groupEmotions(stats, Emotion.disgust),
    groupEmotions(stats, Emotion.sadness),
    groupEmotions(stats, Emotion.sandess),
    groupEmotions(stats, Emotion.joy),
    groupEmotions(stats, Emotion.love),
    groupEmotions(stats, Emotion.confusion),
    groupEmotions(stats, Emotion.surprise),
    groupEmotions(stats, Emotion.excitement),
    groupEmotions(stats, Emotion.shame),
  ];

  const sorted = emotions.sort((a, b) => b.relevance - a.relevance);
  const emotion = sorted[0];
  if (emotion && emotion.relevance > 0) {
    return emotion;
  } else {
    return null;
  }
};



// WEBPACK FOOTER //
// ./src/containers/Stats/Logic/EmotionStatsLogic.tsx